import 'path-data-polyfill';

import * as React from 'react';
import {
  FontFacesLoader,
  ResetCss,
  getStickyHeaderHeight,
} from '@tishman/components';
import { actions, store } from '@tishman/components/store';

import { App } from './src/App';
import { LocalBusinessSchema } from './src/components/schema-dot-org/LocalBusinessSchema';
import { newRelic } from './src/utils/new-relic';
import { captureRedditClickId } from './src/utils/reddit-capi';

import type { GatsbyBrowser } from 'gatsby';

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
  if (typeof window !== 'undefined') {
    newRelic.initializeBrowserAgent().catch(console.error);
  }
  // @ts-expect-error(2540) we need to init if not inited yet
  window.dataLayer = window.dataLayer ?? [];

  captureRedditClickId();

  if (process.env.NODE_ENV === 'production') {
    // silence all console.logs
    Object.entries(console).forEach(([key, value]) => {
      if (['error', 'warn'].includes(key)) return;
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      const noop = () => {};
      Object.defineProperty(noop, '_restore', {
        value: () => void Object.defineProperty(console, key, { value }),
      });
      Object.defineProperty(console, key, { value: noop });
    });
  }
};

export const wrapPageElement: GatsbyBrowser['wrapPageElement'] = ({
  element,
  props,
}) => {
  return (
    <React.StrictMode>
      <ResetCss />
      <FontFacesLoader>
        <App {...props}>{element}</App>
        <LocalBusinessSchema />
      </FontFacesLoader>
    </React.StrictMode>
  );
};

/**
 * Don't update scroll position if the navigation is occurring
 * via an 'anchor link', i.e., `<Link to="#id">`.
 *
 * We opt out of automatic scroll updating in this case because
 * `AnchorSection` implements an animated scroll effect for anchor links.
 *
 * @see AnchorLink
 * @see AnchorSection
 */
export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({
  prevRouterProps,
  routerProps,
}) => {
  // if we're on the same page, this (should) be managed by `AnchorLink`, so
  // let's not let gatsby handle it.
  if (
    prevRouterProps &&
    prevRouterProps.location.pathname === routerProps.location.pathname &&
    routerProps.location.hash
  ) {
    return false;
  }

  // no idea why, but gatsby is completely un-mounting and re-mounting the
  // entire DOM, so navigating to another page with a hash (not handled by
  // `AnchorLink`) causes the page to load scrolled to the bottom.
  //
  // so, we check first if target exists, then if it does, refresh the ref
  // on the next tick (after gatsby has un- and re-mounted the DOM) and scroll
  // that element into view.
  if (routerProps.location.hash) {
    const target = document.getElementById(routerProps.location.hash.slice(1));

    if (target) {
      store.dispatch(actions.anchorSections.setIsAnimating(true));

      setTimeout(() => {
        const targetId = routerProps.location.hash.slice(1);
        const target = document.getElementById(targetId);

        if (!target) {
          store.dispatch(actions.anchorSections.setIsAnimating(false));
          return;
        }

        // Check for custom scroll offset in data attributes
        const dataOffsetX = parseFloat(
          target.getAttribute('data-scroll-offset-x') || '0',
        );
        const dataOffsetY = parseFloat(
          target.getAttribute('data-scroll-offset-y') || '0',
        );

        // Set up a monitor to detect when scrolling has finished
        let boundingClientRect = target.getBoundingClientRect();
        let matchingFrameCount = 0;

        requestAnimationFrame(function checkScrollPosition() {
          const boundingClientRect_ = target.getBoundingClientRect();

          if (boundingClientRect.top === boundingClientRect_.top) {
            matchingFrameCount++;

            if (matchingFrameCount > 2) {
              store.dispatch(actions.anchorSections.setIsAnimating(false));

              return;
            }
          } else {
            boundingClientRect = boundingClientRect_;
            matchingFrameCount = 0;
          }

          requestAnimationFrame(checkScrollPosition);
        });

        const stickyHeaderHeight = getStickyHeaderHeight();

        // Calculate the total offset (secondary menu bar + custom offset)
        const totalOffsetY = stickyHeaderHeight + dataOffsetY;

        // For internal navigation, use scrollIntoView with offset adjustment
        const scrollingElement =
          document.scrollingElement || document.documentElement;
        scrollingElement.scrollTo({
          top:
            target.getBoundingClientRect().top + window.scrollY - totalOffsetY,
          left:
            target.getBoundingClientRect().left + window.scrollX + dataOffsetX,
          behavior: 'smooth',
        });
      }, 100);

      return false;
    }
  }

  return true;
};
